import { type StyleProp, StyleSheet, type ViewStyle } from "react-native";
import { Calendar, type CalendarDate, type MarkedDates } from "rn-calendar-component";

import { isNative, isWeb, useToday } from "@stepapp/core";

import { tz } from "@date-fns/tz";
import { Block, Loader, Typography, spacing, useBreakpoints } from "@stepapp/ui";
import { format, isSameMonth, isSameWeek, startOfWeek } from "date-fns";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks";
import ErrorMessage from "../ErrorMessage";
import CalendarArrows from "./CalendarArrows";
import CalendarMonthTitle from "./CalendarMonthTitle";
import { HoursPicker } from "./HoursPicker";
import calendarTheme from "./calendarTheme";

type Props = {
  selectedDate: string;
  markedDates: MarkedDates;
  handleDayPress: (date: Omit<CalendarDate, "dayOfWeek">) => void;
  handleHourPress: (hour: string) => void;
  resetSelectedDate: () => void;
  hours: { hour: string; available: boolean }[];
  selectedHour: string;
  errorMsg?: string;
  loading: boolean;
  isReservationFlow?: boolean;
  containerStyles?: StyleProp<ViewStyle>;
  startDate?: string;
};

const ServiceWeekCalendar = ({
  selectedDate,
  markedDates,
  handleDayPress,
  hours,
  handleHourPress,
  resetSelectedDate,
  selectedHour,
  errorMsg,
  loading,
  isReservationFlow = false,
  containerStyles,
  startDate,
}: Props) => {
  const { t } = useTranslation();
  const today = useToday();
  const { sm } = useBreakpoints();
  const { startDate: calendarStartDate, endDate: calendarEndDate } = useAppSelector(
    ({ calendar }) => calendar,
  );
  const configTimezone = useAppSelector(({ appConfig }) => appConfig.timezone);

  const webRowView = sm && isReservationFlow;
  const marginRight = spacing.unit * (isReservationFlow ? 6 : 10);

  const isCurrentWeek = isSameWeek(today, calendarStartDate, { in: tz(configTimezone) });
  const startISODate = isCurrentWeek
    ? format(startOfWeek(today, { in: tz(configTimezone) }), "yyyy-MM-dd")
    : calendarStartDate;

  const isNextMonth = !isSameMonth(calendarStartDate, calendarEndDate);

  const calendarTitle = selectedDate
    ? `${format(new Date(selectedDate), "dd MMMM yyyy", { in: tz(configTimezone) })}${selectedHour && `, ${selectedHour}`}`
    : `${isNextMonth ? `${format(new Date(calendarStartDate || today), "LLLL")}/` : ""}${format(new Date(calendarEndDate || today), "LLLL yyyy")}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (!startDate) return;

    const isCurrentWeek = isSameWeek(calendarStartDate, startDate || "", {
      in: tz(configTimezone),
    });

    !isCurrentWeek && resetSelectedDate();
  }, [calendarStartDate]);

  return (
    <Block wrap={webRowView} style={[containerStyles, styles.container]}>
      {loading && (
        <Loader
          type="absolute"
          containerStyles={isWeb ? [] : [styles.loader, { right: marginRight }]}
        />
      )}
      <Block style={styles.calendar}>
        <Calendar
          key={`${startISODate}-${calendarEndDate}`}
          horizontal
          showsHorizontalScrollIndicator={false}
          scrollEnabled={false}
          // @ts-expect-error
          ArrowsComponent={CalendarArrows}
          // @ts-expect-error
          MonthTitleComponent={() =>
            CalendarMonthTitle({ title: calendarTitle, theme: calendarTheme.monthTitle })
          }
          endISODate={calendarEndDate}
          firstDay={1}
          hideExtraDays={false}
          hideArrows={false}
          locale="pl"
          markedDates={markedDates}
          onDayPress={handleDayPress}
          startISODate={startISODate}
          style={styles.calendar}
          theme={calendarTheme}
        />
      </Block>

      <Block paddingVertical={2}>
        <Typography variant={webRowView ? "paragraphBold" : "label"}>
          {isWeb
            ? t("pages.serviceDateAndAddress.chooseHour")
            : t("screens.serviceCalendar.chooseHour")}
        </Typography>
      </Block>

      <HoursPicker
        selectHour={handleHourPress}
        hours={hours}
        selectedHour={selectedHour}
        isDatePicked={!!selectedDate}
      />

      {isWeb && <ErrorMessage message={errorMsg || undefined} />}
    </Block>
  );
};

export default ServiceWeekCalendar;

const styles = StyleSheet.create({
  container: {
    //@ts-expect-error
    display: isNative ? "flex" : "grid",
  },
  calendarContainer: {
    maxWidth: spacing.unit * 60,
  },
  calendar: {
    minHeight: spacing.unit * 17,
  },
  loader: {
    top: spacing.unit,
    zIndex: 10,
  },
});
